import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { AlarmClock } from 'lucide-react';
const STORAGE_KEY = 'pickleball_instructor_ready_countdown_target';
const ONE_DAY = 24 * 60 * 60 * 1000;
function getInitialTarget(): number {
  if (typeof window === 'undefined') return Date.now() + ONE_DAY;
  const stored = window.localStorage.getItem(STORAGE_KEY);
  if (stored) {
    const parsed = parseInt(stored, 10);
    if (!Number.isNaN(parsed) && parsed > Date.now()) return parsed;
  }
  const target = Date.now() + ONE_DAY;
  try {
    window.localStorage.setItem(STORAGE_KEY, target.toString());
  } catch {
    /* localStorage unavailable */
  }
  return target;
}
function pad(value: number) {
  return value.toString().padStart(2, '0');
}
export function CountdownTimer() {
  const [target, setTarget] = useState<number | null>(null);
  const [remaining, setRemaining] = useState(ONE_DAY);
  useEffect(() => setTarget(getInitialTarget()), []);
  useEffect(() => {
    if (!target) return;
    const tick = () => {
      const difference = target - Date.now();
      if (difference <= 0) {
        const nextTarget = Date.now() + ONE_DAY;
        try {
          window.localStorage.setItem(STORAGE_KEY, nextTarget.toString());
        } catch {
          /* localStorage unavailable */
        }
        setTarget(nextTarget);
        setRemaining(ONE_DAY);
        return;
      }
      setRemaining(difference);
    };
    tick();
    const intervalId = setInterval(tick, 1000);
    return () => clearInterval(intervalId);
  }, [target]);
  const hours = Math.floor(remaining / (1000 * 60 * 60));
  const minutes = Math.floor(remaining / (1000 * 60) % 60);
  const seconds = Math.floor(remaining / 1000 % 60);
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true
  }} className="mt-10 max-w-3xl mx-auto bg-forest border border-gold/50 rounded-2xl p-6 md:p-8 shadow-[0_10px_30px_rgba(20,67,42,0.25)]" data-id="element-57" data-mp="1pz534ry7kcid">
      <div className="text-center" data-id="element-58" data-mp="2sczhp6qktdy0">
        <div className="flex items-center justify-center gap-2 text-gold-light font-bold tracking-wider uppercase text-sm md:text-base mb-6" data-id="element-59" data-mp="2sby6n29k8rga">
          <AlarmClock size={20} className="animate-pulse" data-id="element-60" data-mp="2jbt00uc6xx36" />
          <span data-id="element-61" data-mp="2ja85h4pvme5b">⏳ The launch price ends soon</span>
        </div>
        <div className="flex items-center justify-center gap-3 md:gap-5 mb-5" data-id="element-62" data-mp="2ebwtoog0ttkg">
          <TimeBox value={pad(hours)} label="hrs" data-id="element-63" data-mp="29eeu3onw8qp4" />
          <span className="text-3xl md:text-5xl font-bold text-gold-light" data-id="element-64" data-mp="29f6gc8vlpc2q">:</span>
          <TimeBox value={pad(minutes)} label="min" data-id="element-65" data-mp="24mdd5pfs6bsr" />
          <span className="text-3xl md:text-5xl font-bold text-gold-light" data-id="element-66" data-mp="1znskqc6rswev">:</span>
          <TimeBox value={pad(seconds)} label="sec" data-id="element-67" data-mp="1zlpj0uw99eub" />
        </div>
        <p className="text-xs md:text-sm font-bold tracking-wider uppercase text-cream mb-8" data-id="element-68" data-mp="1vo1ovu2dzq3h">
          After this, the price goes up — and it won&apos;t come back down.
        </p>
        <div className="space-y-2" data-id="element-69" data-mp="1lpc67z2o21m6">
          <div className="flex justify-between text-[11px] md:text-xs font-bold uppercase tracking-wider" data-id="element-70" data-mp="1lqdp2ppxbseg">
            <span className="text-cream" data-id="element-71" data-mp="18mdpl0d7ad2">🕐 Launch pricing spots remaining</span>
            <span className="text-gold-light" data-id="element-72" data-mp="63iaszkgknx1">Only a few left</span>
          </div>
          <div className="w-full h-2.5 bg-forest-dark rounded-full overflow-hidden border border-gold/30" data-id="element-73" data-mp="b4xuan6zmmdb">
            <motion.div initial={{
            width: 0
          }} whileInView={{
            width: '65%'
          }} viewport={{
            once: true
          }} transition={{
            duration: 1.5,
            ease: 'easeOut'
          }} className="h-full bg-gold rounded-full" data-id="element-74" data-mp="f58b6uwbix6y" />
          </div>
          <p className="text-right text-[11px] md:text-xs text-cream/80 font-medium" data-id="element-75" data-mp="3nk9w3yx1yaxq">
            65% of spots taken
          </p>
        </div>
      </div>
    </motion.div>;
}
function TimeBox({
  value,
  label
}: {
  value: string;
  label: string;
}) {
  return <div className="bg-cream-light border-2 border-gold rounded-xl px-4 md:px-6 py-3 md:py-4 min-w-[80px] md:min-w-[110px]" data-id="element-76" data-mp="2zvqlvdufzo30">
      <div className="flex items-baseline justify-center gap-1" data-id="element-77" data-mp="2gsf25f1e58z2">
        <span className="text-4xl md:text-5xl font-bold text-forest tabular-nums" data-id="element-78" data-mp="2bsspb6ph5uqs">{value}</span>
        <span className="text-xs md:text-sm font-medium text-gold-dark" data-id="element-79" data-mp="2qh6qld78tb58">{label}</span>
      </div>
    </div>;
}